import { AppForm } from '../../../../../../common/forms/app.form';
import { Injectable } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import {
  RoadOccupationStageForm,
  RoadOccupationStageFormValue, toStageFormValue
} from './road-occupation-stage.form';
import {
  LocationDecisionAppEntityLocationDecisionWrite,
  RoadOccupationAppEntityApplicationRead
} from '../../../../../../openapi';

export type PurposeControlType = 'input' | 'textarea';

export type WorkPurposeControlType = 'select' | 'textarea';

export function occupationsToFormValue(occupations: Array<RoadOccupationAppEntityApplicationRead>): Array<RoadOccupationFormValue> {
  return occupations
    .sort((a, b) => (b?.ordinalNumber || 0) - (a?.ordinalNumber || 0))
    .map(v => {
      return {
        stages: [...v?.stages || []]
          .sort((a, b) => (b?.ordinalNumber || 0) - (a?.ordinalNumber || 0))
          .map(v => toStageFormValue(v)) || [],
        locationDecisions: v?.locationDecisions?.map(v => ({
          decisionNumber: v.decisionNumber,
          decisionDate: v.decisionDate
        })) || [],
        id: v?.uuid || '',
        occupationType: v?.occupationType?.id,
        city: v?.city || '',
        purpose: v?.purpose,
        locationDecisionDate: v?.locationDecisionDate || null,
        locationDecisionNumber: v?.locationDecisionNumber || '',
        agreementDate: v?.agreementDate || null,
        agreementNumber: v?.agreementNumber || '',
        typeOfFailure: v?.typeOfFailure,
        surfaceViolation: v?.surfaceViolation,
        street: v?.street,
        workPurpose: v?.workPurpose
      };
    });
}

export interface RoadOccupationFormValue {
  id: string;
  occupationType?: string;
  surfaceViolation?: string;
  city?: string;
  purpose?: string;
  workPurpose?: string;
  typeOfFailure?: string;
  locationDecisionNumber: string;
  locationDecisionDate: Date | null;
  agreementNumber: string | null;
  agreementDate: Date | null;
  stages: RoadOccupationStageFormValue[];
  locationDecisions?: LocationDecisionAppEntityLocationDecisionWrite[];
}

@Injectable()
export class RoadOccupationForm extends AppForm {

  constructor() {
    super({
      id: new FormControl('', { nonNullable: true }),
      occupationType: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      city: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      purpose: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(5000)],
        nonNullable: true
      }),
      stages: new FormArray([
        new RoadOccupationStageForm()
      ])
    });
  }

  public get city(): FormControl | null {
    return this.get('city') as FormControl | null;
  }

  public createWorkPurposeControl(): void {
    this.addControl('workPurpose', new FormControl('', {
      validators: [Validators.required, Validators.maxLength(5000)],
      nonNullable: true
    }));
  }

  public get workPurpose(): FormControl | null {
    return this.get('workPurpose') as FormControl | null;
  }

  public createSurfaceViolationControl(): void {
    this.addControl('surfaceViolation', new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255)],
      nonNullable: true
    }));
  }

  public get surfaceViolation(): FormControl | null {
    return this.get('surfaceViolation') as FormControl | null;
  }

  public createTypeOfFailureControl(): void {
    this.addControl(
      'typeOfFailure',
      new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      })
    );
  }

  public get typeOfFailure(): FormControl | null {
    return this.get('typeOfFailure') as FormControl | null;
  }

  public get occupationType(): FormControl | null {
    return this.get('occupationType') as FormControl | null;
  }

  public get purpose(): FormControl | null {
    return this.get('purpose') as FormControl | null;
  }

  public createDecisionControl(): void {
    this.addControl('locationDecisions', new FormControl([], {
      nonNullable: true,
      validators: [Validators.minLength(1), Validators.maxLength(255)]
    }));
  }

  public get locationDecisions(): FormControl | null {
    return this.get('locationDecisions') as FormControl | null;
  }

  public addStage(): void {
    this.stages.push(new RoadOccupationStageForm());
  }

  public removeStage(i: number): void {
    this.stages.removeAt(i);
  }

  public get stages(): FormArray {
    return this.get('stages') as FormArray;
  }

  public getStageForms(): RoadOccupationStageForm[] {
    return this.stages.controls as RoadOccupationStageForm[];
  }

  public override get valid(): boolean {
    return super.valid && !!this.stages.controls.map(c => c.valid).filter(v => v).length;
  }

  public get values(): RoadOccupationFormValue {
    return {
      ...this.value,
      stages: this.getStageForms().map(v => v.values)
    } as RoadOccupationFormValue;
  }

  public override patchFormValues(value: RoadOccupationFormValue) {
    super.patchFormValues(value);
    this.stages.clear();
    (value?.stages || []).forEach(stage => {
      const sf = new RoadOccupationStageForm();
      sf.patchValue({
        ...stage
      });
      this.stages.push(sf);
    });
  }
}
